import { datadogLogs, LogsEventDomainContext } from '@datadog/browser-logs';
import { enablePatches } from 'immer';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import * as CookieConsent from 'vanilla-cookieconsent';
import { API_BASE_URL } from '@config/constants';
import { cookieConsentConfig } from '@config/cookie-consent';
import { AuthProvider } from '@context/AuthContext';
import { CurrentTimeProvider } from '@context/CurrentTimeContext';
import { APP_ENV, APP_VERSION } from '@helpers/appEnv';
import { htmlSanitize } from '@helpers/htmlSanitize';
import { nonNull } from '@helpers/non-null';
import { setSwCta } from '@helpers/swCta';
import Auth0ProviderWithHistory from '@hoc';
import { ErrorBoundary } from '@pages/ErrorPage';
import store from '@redux/store';
import { MixpanelService } from '@services/mixpanelService';
import { registerServiceWorker } from 'src/register-sw';
import { App } from './App';
import '@styles/main.less';

setSwCta();

if (APP_VERSION && APP_VERSION !== 'unknown') {
  datadogLogs.init({
    trackingConsent: 'not-granted',
    service: 'frontend',
    env: APP_ENV,
    version: APP_VERSION,
    clientToken: nonNull(
      process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
      'REACT_APP_DATADOG_CLIENT_TOKEN'
    ),
    forwardErrorsToLogs: true,
    forwardConsoleLogs: 'all',
    forwardReports: 'all',
    trackSessionAcrossSubdomains: true,
    sessionSampleRate: 100,
    proxy: `${API_BASE_URL}dtdg/v6`,
    beforeSend(event, context) {
      if (event.origin === 'network') {
        const ctx = context as LogsEventDomainContext<'network'>;
        if (ctx.isAborted) return false;
        if (
          event.http?.url?.includes('.hubspot.com/') ||
          event.http?.url?.includes('.hscollectedforms.net/') ||
          event.http?.url?.includes('.hubapi.com/')
        ) {
          return false;
        }
      }
      if (event.error?.kind === 'CanceledError') {
        return false;
      }
      // reactour, while a step's element is rendering: found once rendered
      if (
        event.origin === 'console' &&
        event.message.startsWith("Doesn't find a DOM node '.tour-")
      ) {
        return false;
      }
      // Thrown by a script of another origin (reCAPTCHA, HubSpot, Hotjar):
      // the browser hides the error itself
      if (event.message === 'Uncaught "Script error."') {
        return false;
      }
      return true;
    }
  });
}

registerServiceWorker();

// A lazily loaded chunk failed to load, most likely one a deploy has replaced
// since this page loaded: reload to get the current index.html. Once per
// minute, so a chunk that keeps failing reaches the ErrorBoundary instead of
// reloading forever
const preloadErrorReloadKey = 'vite-preload-error-reload';
window.addEventListener('vite:preloadError', (event) => {
  if (!navigator.onLine) return;
  try {
    const lastReload = Number(sessionStorage.getItem(preloadErrorReloadKey));
    if (Date.now() - lastReload < 60_000) return;
    sessionStorage.setItem(preloadErrorReloadKey, `${Date.now()}`);
  } catch {
    return;
  }
  event.preventDefault();
  window.location.reload();
});

enablePatches();

const originalPushState = window.history.pushState.bind(window.history);
const originalReplaceState = window.history.replaceState.bind(window.history);
function isSameURL(url?: string | URL | null) {
  if (url == null) return null;
  if (typeof url === 'string') {
    const aEl = document.createElement('a');
    aEl.href = url;
    return aEl.href === window.location.href;
  }
  return url.toString() === window.location.href;
}
window.history.pushState = (state, title, url) => {
  if (!isSameURL(url)) {
    originalPushState(state, title, url);
  }
};
window.history.replaceState = (state, title, url) => {
  if (!isSameURL(url)) {
    originalReplaceState(state, title, url);
  }
};

if (typeof window.trustedTypes !== 'undefined') {
  window.trustedTypes.createPolicy('default', {
    createHTML: (input) => htmlSanitize(input, false),
    createScriptURL: (input) => input,
    createScript: (input) => input
  });
}

CookieConsent.run({
  ...cookieConsentConfig,
  autoShow: false,
  onConsent: ({ cookie }) => {
    if (cookie.categories.includes('analytics')) {
      datadogLogs.setTrackingConsent('granted');
      MixpanelService.onConsent();
    }
  }
});

createRoot(nonNull(document.getElementById('root'))).render(
  <BrowserRouter>
    <ErrorBoundary>
      <Provider store={store}>
        <Auth0ProviderWithHistory>
          <AuthProvider>
            <CurrentTimeProvider>
              <App />
            </CurrentTimeProvider>
          </AuthProvider>
        </Auth0ProviderWithHistory>
      </Provider>
    </ErrorBoundary>
  </BrowserRouter>
);
